import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { defer, Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { RuntimeConfigService } from './runtime-config.service';
import { User } from '../app/models/user.model';
import { IdentitySwitchService } from './identity-switch.service';

export interface LinkedGuardian {
  GuardianUserId: number;
  FirstName: string;
  LastName: string;
  Email: string;
  VerificationMethod: string;
}

export interface GuardianLinkResult {
  Status:
    | 'NotStaff'
    | 'AlreadyLinked'
    | 'Linked'
    | 'NeedEmail'
    | 'CodeSent'
    | 'NotFound'
    | 'InvalidCode'
    | 'Expired'
    | 'TooManyAttempts';
  Guardian?: LinkedGuardian | null;
  MaskedEmail?: string | null;
}

/**
 * Client for the guardian-link API: a Staff member linking and switching to their own
 * Guardian ("parent") account.
 */
@Injectable({
  providedIn: 'root'
})
export class GuardianLinkService {
  private apiUrl: string;

  /**
   * Emits whenever the staff -> guardian link is created or removed. Views that cache the
   * linked-guardian state (e.g. the header menu) subscribe to re-fetch, since the link can be
   * changed from the Profile page without their own load lifecycle re-running.
   */
  private readonly linkChangedSubject = new Subject<void>();
  public readonly linkChanged$ = this.linkChangedSubject.asObservable();

  /** Notifies subscribers that the link was created or removed. */
  notifyLinkChanged(): void {
    this.linkChangedSubject.next();
  }

  constructor(private http: HttpClient,
              private runtimeConfig: RuntimeConfigService,
              private identitySwitch: IdentitySwitchService) {
    this.apiUrl = this.runtimeConfig.currentConfig.apiUrl + '/GuardianLink';
    this.runtimeConfig.config$.subscribe(config => {
      this.apiUrl = config.apiUrl + '/GuardianLink';
    });
  }

  getLinkedGuardian(): Observable<LinkedGuardian | null> {
    return this.http.get<LinkedGuardian | null>(`${this.apiUrl}/GetLinkedGuardianAsync`);
  }

  initiateLink(email?: string): Observable<GuardianLinkResult> {
    return this.http.post<GuardianLinkResult>(`${this.apiUrl}/InitiateLinkAsync`, { email: email ?? null });
  }

  confirmLink(email: string, code: string): Observable<GuardianLinkResult> {
    return this.http.post<GuardianLinkResult>(`${this.apiUrl}/ConfirmLinkAsync`, { email, code });
  }

  unlink(): Observable<GuardianLinkResult> {
    return this.http.post<GuardianLinkResult>(`${this.apiUrl}/UnlinkAsync`, {});
  }

  switchToGuardian(): Observable<User> {
    // The identity changes on return, and this page keeps running until the reload commits, so
    // hold parent-portal calls across the transition (see IdentitySwitchService).
    return defer(() => {
      this.identitySwitch.begin();
      return this.http.post<User>(`${this.apiUrl}/SwitchToGuardianAsync`, {});
    }).pipe(
      // Nothing changed on a failure — release the hold (see UserProfileService.clearImpersonation).
      tap({ error: () => this.identitySwitch.end() })
    );
  }
}
